import React, { useEffect, useState, useCallback } from 'react';
import { apiGetUsers, apiToggleUser, apiDeleteUser, apiRegister } from '../services/api';
import toast from 'react-hot-toast';

const fmtDate = (d) => d ? new Date(d).toLocaleDateString('en-IN', { day: '2-digit', month: 'short', year: 'numeric' }) : '—';

const emptyForm = { name: '', email: '', password: '', role: 'counselor' };

export default function UsersPage() {
    const [users, setUsers] = useState([]);
    const [loading, setLoading] = useState(true);
    const [showForm, setShowForm] = useState(false);
    const [form, setForm] = useState(emptyForm);
    const [saving, setSaving] = useState(false);
    const [formErrors, setFormErrors] = useState({});
    const [toggling, setToggling] = useState(null);

    const load = useCallback(async () => {
        setLoading(true);
        try {
            const res = await apiGetUsers();
            setUsers(res.data?.data || []);
        } catch {
            toast.error('Failed to load users');
        } finally {
            setLoading(false);
        }
    }, []);

    useEffect(() => { load(); }, [load]);

    const handleChange = (e) => {
        setForm(f => ({ ...f, [e.target.name]: e.target.value }));
        if (formErrors[e.target.name]) setFormErrors(er => ({ ...er, [e.target.name]: undefined }));
    };

    const validate = () => {
        const errs = {};
        if (!form.name.trim()) errs.name = 'Name required';
        if (!form.email.trim()) errs.email = 'Email required';
        if (!/^\S+@\S+\.\S+$/.test(form.email)) errs.email = 'Invalid email';
        if (!form.password || form.password.length < 6) errs.password = 'Min 6 characters';
        return errs;
    };

    const handleCreate = async (e) => {
        e.preventDefault();
        const errs = validate();
        if (Object.keys(errs).length) { setFormErrors(errs); return; }
        setSaving(true);
        try {
            await apiRegister(form);
            toast.success('User created successfully!');
            setForm(emptyForm);
            setShowForm(false);
            load();
        } catch (err) {
            toast.error(err.response?.data?.message || 'Create failed');
        } finally {
            setSaving(false);
        }
    };

    const handleToggle = async (id, isActive) => {
        setToggling(id);
        try {
            await apiToggleUser(id);
            toast.success(isActive ? 'User deactivated' : 'User activated');
            load();
        } catch {
            toast.error('Toggle failed');
        } finally {
            setToggling(null);
        }
    };

    const handleDelete = async (id, name) => {
        if (!window.confirm(`Permanently delete user "${name}"? This cannot be undone.`)) return;
        try {
            await apiDeleteUser(id);
            toast.success('User deleted');
            load();
        } catch (e) {
            toast.error(e.response?.data?.message || 'Delete failed');
        }
    };

    const admins    = users.filter(u => u.role === 'admin');
    const counselors = users.filter(u => u.role === 'counselor');

    return (
        <div className="page">
            <div className="page-header">
                <div>
                    <h1 className="page-title">Users</h1>
                    <p className="page-subtitle">{users.length} total user{users.length !== 1 ? 's' : ''}</p>
                </div>
                <button
                    id="add-user-btn"
                    className="btn btn-primary"
                    onClick={() => { setShowForm(s => !s); setForm(emptyForm); setFormErrors({}); }}
                >
                    {showForm ? '✕ Cancel' : '➕ Add User'}
                </button>
            </div>

            {/* Create User Form */}
            {showForm && (
                <div className="card" style={{ marginBottom: 24 }}>
                    <div className="card-header"><span className="card-title">Create New User</span></div>
                    <div className="card-body">
                        <form id="create-user-form" onSubmit={handleCreate} noValidate>
                            <div className="form-row">
                                <div className="form-group">
                                    <label htmlFor="user-name">Full Name *</label>
                                    <input id="user-name" name="name" value={form.name} onChange={handleChange} placeholder="e.g. Jane Counselor" />
                                    {formErrors.name && <span style={{ color: 'var(--danger)', fontSize: '0.78rem' }}>{formErrors.name}</span>}
                                </div>
                                <div className="form-group">
                                    <label htmlFor="user-email">Email Address *</label>
                                    <input id="user-email" name="email" type="email" value={form.email} onChange={handleChange} placeholder="jane@crm.com" />
                                    {formErrors.email && <span style={{ color: 'var(--danger)', fontSize: '0.78rem' }}>{formErrors.email}</span>}
                                </div>
                            </div>
                            <div className="form-row">
                                <div className="form-group">
                                    <label htmlFor="user-password">Password *</label>
                                    <input id="user-password" name="password" type="password" value={form.password} onChange={handleChange} placeholder="Min 6 characters" />
                                    {formErrors.password && <span style={{ color: 'var(--danger)', fontSize: '0.78rem' }}>{formErrors.password}</span>}
                                </div>
                                <div className="form-group">
                                    <label htmlFor="user-role">Role</label>
                                    <select id="user-role" name="role" value={form.role} onChange={handleChange}>
                                        <option value="counselor">Counselor</option>
                                        <option value="admin">Admin</option>
                                    </select>
                                </div>
                            </div>
                            <div className="form-actions">
                                <button type="button" className="btn btn-ghost" onClick={() => setShowForm(false)}>Cancel</button>
                                <button id="create-user-submit" type="submit" className="btn btn-primary" disabled={saving}>
                                    {saving ? <><span className="spinner" style={{ width: 14, height: 14, borderWidth: 2 }} /> Creating…</> : '✔ Create User'}
                                </button>
                            </div>
                        </form>
                    </div>
                </div>
            )}

            {loading ? (
                <div className="loading-center"><div className="spinner" /><span>Loading users…</span></div>
            ) : (
                <>
                    {/* Admins Table */}
                    <div className="card" style={{ marginBottom: 20 }}>
                        <div className="card-header">
                            <span className="card-title">🛡 Admins ({admins.length})</span>
                        </div>
                        {admins.length === 0 ? (
                            <div className="empty-state"><p>No admins</p></div>
                        ) : (
                            <div className="table-wrapper">
                                <table className="data-table" id="admins-table">
                                    <thead>
                                        <tr><th>Name</th><th>Email</th><th>Status</th><th>Joined</th><th>Actions</th></tr>
                                    </thead>
                                    <tbody>
                                        {admins.map(u => (
                                            <tr key={u.id} id={`user-row-${u.id}`}>
                                                <td>
                                                    <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
                                                        <div className="user-avatar-sm" style={{ width: 30, height: 30, fontSize: '0.75rem' }}>{u.name?.[0]?.toUpperCase()}</div>
                                                        <strong>{u.name}</strong>
                                                    </div>
                                                </td>
                                                <td>{u.email}</td>
                                                <td><span className={`badge badge--${u.is_active ? 'active' : 'inactive'}`}>{u.is_active ? 'Active' : 'Inactive'}</span></td>
                                                <td style={{ whiteSpace: 'nowrap' }}>{fmtDate(u.created_at)}</td>
                                                <td>
                                                    <div className="action-cell">
                                                        <button
                                                            id={`toggle-user-${u.id}`}
                                                            className={`btn btn-sm ${u.is_active ? 'btn-warning' : 'btn-success'}`}
                                                            disabled={toggling === u.id}
                                                            onClick={() => handleToggle(u.id, u.is_active)}
                                                        >
                                                            {toggling === u.id ? '…' : u.is_active ? '🔒 Deactivate' : '🔓 Activate'}
                                                        </button>
                                                    </div>
                                                </td>
                                            </tr>
                                        ))}
                                    </tbody>
                                </table>
                            </div>
                        )}
                    </div>

                    {/* Counselors Table */}
                    <div className="card">
                        <div className="card-header">
                            <span className="card-title">👤 Counselors ({counselors.length})</span>
                        </div>
                        {counselors.length === 0 ? (
                            <div className="empty-state">
                                <div className="empty-state-icon">👤</div>
                                <p>No counselors yet. <button className="btn btn-ghost btn-sm" onClick={() => setShowForm(true)}>Add one</button></p>
                            </div>
                        ) : (
                            <div className="table-wrapper">
                                <table className="data-table" id="counselors-table">
                                    <thead>
                                        <tr><th>Name</th><th>Email</th><th>Status</th><th>Joined</th><th>Actions</th></tr>
                                    </thead>
                                    <tbody>
                                        {counselors.map(u => (
                                            <tr key={u.id} id={`user-row-${u.id}`}>
                                                <td>
                                                    <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
                                                        <div className="user-avatar-sm" style={{ width: 30, height: 30, fontSize: '0.75rem' }}>{u.name?.[0]?.toUpperCase()}</div>
                                                        <strong>{u.name}</strong>
                                                    </div>
                                                </td>
                                                <td>{u.email}</td>
                                                <td><span className={`badge badge--${u.is_active ? 'active' : 'inactive'}`}>{u.is_active ? 'Active' : 'Inactive'}</span></td>
                                                <td style={{ whiteSpace: 'nowrap' }}>{fmtDate(u.created_at)}</td>
                                                <td>
                                                    <div className="action-cell">
                                                        <button
                                                            id={`toggle-user-${u.id}`}
                                                            className={`btn btn-sm ${u.is_active ? 'btn-warning' : 'btn-success'}`}
                                                            disabled={toggling === u.id}
                                                            onClick={() => handleToggle(u.id, u.is_active)}
                                                        >
                                                            {toggling === u.id ? '…' : u.is_active ? '🔒 Deactivate' : '🔓 Activate'}
                                                        </button>
                                                        <button
                                                            id={`delete-user-${u.id}`}
                                                            className="btn btn-danger btn-sm"
                                                            onClick={() => handleDelete(u.id, u.name)}
                                                        >🗑</button>
                                                    </div>
                                                </td>
                                            </tr>
                                        ))}
                                    </tbody>
                                </table>
                            </div>
                        )}
                    </div>
                </>
            )}
        </div>
    );
}
